import { Column } from '../components/DataTable/DataTable';
import { formatDate as formatUserDate, formatLocaleDateTime } from '../config/appConfig';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

export interface ExportToPdfData {
  sortedData: any[];
  tableColumns: Column<any>[];
  entityName: string;
  exportFileName: string;
  formatDate?: (dateString?: string) => string;
}

/**
 * Export data to PDF format
 * @param exportData - Data to export
 */
export const exportToPDF = async (exportData: ExportToPdfData): Promise<void> => {
  const { sortedData, tableColumns, entityName, exportFileName, formatDate } = exportData;

  // Filter out actions column
  const columnsToExport = tableColumns.filter((col) => col.key !== 'actions');

  // Helper function to format array values
  const formatArrayValue = (value: any, column: Column<any>, row: any): string => {
    if (!value && !(column as any).displayField) return '';

    let arrayValue = value;
    if ((column as any).displayField && row && row[(column as any).displayField]) {
      arrayValue = row[(column as any).displayField];
    }

    if (!Array.isArray(arrayValue)) {
      if (typeof arrayValue === 'string' && arrayValue.trim().startsWith('[')) {
        try {
          arrayValue = JSON.parse(arrayValue);
        } catch {
          return String(value || '');
        }
      } else {
        return String(value || '');
      }
    }

    if (arrayValue.length === 0) return '';

    if (arrayValue.length > 0 && typeof arrayValue[0] === 'object') {
      const names = arrayValue
        .map((item: any) => {
          return (
            item.userDisplayName ||
            item.name ||
            item.title ||
            item.label ||
            (item.userFirstName && item.userLastName
              ? `${item.userFirstName} ${item.userLastName}`
              : '')
          );
        })
        .filter((name: string) => name);

      return names.join(', ');
    }

    return arrayValue.join(', ');
  };

  // Helper function to format dates
  const formatDateValue = (value: any, column: Column<any>): string => {
    if (!value) return '';
    try {
      const dateStr = String(value);
      if (dateStr.includes('T') || dateStr.match(/^\d{4}-\d{2}-\d{2}/)) {
        if (formatDate) {
          return formatDate(value instanceof Date ? value.toISOString() : value);
        }
        return formatUserDate(value instanceof Date ? value.toISOString() : value);
      }
      return String(value);
    } catch {
      return String(value || '');
    }
  };

  // Prepare headers
  const headers = columnsToExport.map((col) => col.header || String(col.key));

  // Prepare data rows
  const rows = sortedData.map((row) => {
    return columnsToExport.map((col) => {
      let value = row[String(col.key)];

      // Check displayField
      if ((value === undefined || value === null) && (col as any).displayField && row[(col as any).displayField] !== undefined) {
        value = row[(col as any).displayField];
      }

      if (value === undefined || value === null) {
        value = '';
      }

      // Format dates
      if (col.render || (col as any).cellRenderer === 'date') {
        value = formatDateValue(value, col);
      }
      // Format arrays
      else if ((col as any).cellRenderer === 'array' || Array.isArray(value)) {
        value = formatArrayValue(value, col, row);
      }
      // Handle objects
      else if (typeof value === 'object' && value !== null) {
        if (value.name || value.title || value.label) {
          value = value.name || value.title || value.label;
        } else {
          value = JSON.stringify(value);
        }
      }

      return String(value || '');
    });
  });

  // Create PDF document
  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'mm',
    format: 'a4',
  });

  // Add title
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(`${entityName} List`, 14, 15);

  // Add date
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const dateTime = formatLocaleDateTime(new Date());
  const pageWidth = doc.internal.pageSize.getWidth();
  const dateWidth = doc.getTextWidth(dateTime);
  doc.text(dateTime, pageWidth - dateWidth - 14, 15);

  // Add table
  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: 20,
    styles: {
      fontSize: 8,
      cellPadding: 2,
      overflow: 'linebreak',
      cellWidth: 'wrap',
    },
    headStyles: {
      fillColor: [74, 85, 104], // Gray-600
      textColor: 255,
      fontStyle: 'bold',
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251], // Gray-50
    },
    margin: { top: 20, left: 14, right: 14, bottom: 14 },
  });

  // Save PDF
  doc.save(`${exportFileName}.pdf`);
};
